import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ImageUploadZone, ImageData, DEFAULT_EMPTY_ZONE_HEIGHT } from './ImageUploadZone';
import { copyImageUrlToClipboard } from '../../utils/clipboardUtils';
import { ImageActions } from './ImageActions';
import { Lightbox } from './Lightbox';
import { generateMediaFilename } from '../../utils/markdownToRtf';
import { useReactFlow, useStoreApi, useStore, Node } from '@xyflow/react';
import InpaintEditor from '../inpaint/InpaintEditor';
import { getOutputNodeDimensions, getNodeDimensions } from '../../config/nodeRegistry';
import { recordUserAction } from '../../services/FlowHistoryRef';

/** Registry box for an Image Container: 420x300 at the time of writing. */
const IMAGE_PANEL_DEFAULTS = getNodeDimensions('imagePanel');

/**
 * How tall an EMPTY Image Container's body should be.
 *
 * ## Why this has to be computed at all
 *
 * `src/styles/components/_ui-components.css` gives `.react-flow__node` an
 * `height: auto !important`, which beats the inline `height: 300px` React Flow
 * writes from `node.height`. So `.base-node`'s `height: 100%` resolves against
 * an indefinite parent and degrades to `auto`, and so does every `height: 100%`
 * below it — including this panel's. Nothing in the chain can "fill" the node,
 * because there is no definite height to fill.
 *
 * A FILLED panel does not notice: the `<img>` supplies its own height. An EMPTY
 * one has nothing to supply one, so it collapsed to the drop zone's 160px floor
 * and painted a header plus a stub inside a 420x300 box — which reads as a
 * half-rendered node rather than an empty one.
 *
 * ## The rule
 *
 * The empty body takes the node's own `height`, which is the same number the
 * filled body ends up at (`handleImageUpload` writes `width / aspectRatio` into
 * `node.height`, and the image then renders at it). So an empty panel occupies
 * the box a dropped image would occupy, and it follows a user resize.
 *
 * The registry default stands in when the node has no stored height, and the
 * old 160px floor stays as a lower bound so a very short panel still shows the
 * icon and both lines of copy.
 */
export function emptyPanelBodyHeight(nodeHeight?: number | null): number {
  const base =
    typeof nodeHeight === 'number' && Number.isFinite(nodeHeight) && nodeHeight > 0
      ? nodeHeight
      : IMAGE_PANEL_DEFAULTS.height;
  return Math.max(base, DEFAULT_EMPTY_ZONE_HEIGHT);
}

interface ImagePanelProps {
  nodeId: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  targetWidth?: number;
  isUploading?: boolean;
  onSendToDevice?: (url: string, type: string) => void;
  onSetFlowThumbnail?: (url: string) => void;
  disableContextMenu?: boolean;
  outputLabel?: string; // Used as filename slug when downloading
}

export const ImagePanel: React.FC<ImagePanelProps> = ({
  nodeId,
  value,
  onChange,
  disabled = false,
  targetWidth: _targetWidth = 420,
  isUploading = false,
  onSendToDevice,
  onSetFlowThumbnail,
  disableContextMenu = false,
  outputLabel,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [inpaintOpen, setInpaintOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [imgDimensions, setImgDimensions] = useState<{ w: number; h: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const { setNodes, setEdges, getNode, getNodes: _getNodes } = useReactFlow();
  const storeApi = useStoreApi();

  // Subscribed rather than read once, so resizing an empty panel resizes its
  // drop zone with it. Returns a number, so the default equality check is fine.
  const nodeHeight = useStore((s) => s.nodeLookup.get(nodeId)?.height);
  const emptyHeight = emptyPanelBodyHeight(nodeHeight);

  // Detect image dimensions when value changes (e.g., on flow load)
  // This ensures preloadedWidth/preloadedHeight are set for proper aspect ratio locking
  // NOTE: We use storeApi (stable reference from useStoreApi) instead of getNode from useReactFlow.
  // getNode changes reference on every ReactFlow store update, which would cause this effect to
  // re-fire on every render, creating an infinite render loop.
  useEffect(() => {
    if (!value) {
      setImgDimensions(null);
      return;
    }

    // storeApi.getState() reads fresh node data without depending on an unstable reference
    const nodeLookup = storeApi.getState().nodeLookup;
    const currentNode = nodeLookup.get(nodeId);
    if (currentNode) {
      const nodeData = currentNode.data as Record<string, unknown>;
      if (nodeData.preloadedWidth && nodeData.preloadedHeight) {
        // Dimensions already stored — use them for display
        setImgDimensions({ w: nodeData.preloadedWidth as number, h: nodeData.preloadedHeight as number });
        return;
      }
    }

    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const aspectRatio = width / height;

      setImgDimensions({ w: width, h: height });

      setNodes((nodes: Node[]) =>
        nodes.map((node: Node) => {
          if (node.id === nodeId) {
            const currentWidth = node.width || 420;
            const calculatedHeight = Math.round(currentWidth / aspectRatio);

            return {
              ...node,
              height: calculatedHeight,
              data: {
                ...node.data,
                preloadedWidth: width,
                preloadedHeight: height,
              },
            };
          }
          return node;
        })
      );
    };
    img.src = value;
  }, [value, nodeId, storeApi, setNodes]);

  // Close context menu when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as globalThis.Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', closeMenu, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', closeMenu, true);
      };
    }
    return undefined;
  }, [contextMenu]);

  // Handle context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (value && !disableContextMenu) {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  }, [value, disableContextMenu]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (value) {
      const link = document.createElement('a');
      link.href = value;
      link.download = generateMediaFilename('image', { url: value, nodeTitle: outputLabel });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [value, outputLabel]);

  const handleImageUpload = (imageData: ImageData) => {
    recordUserAction('Set image', { touchesResults: [nodeId] });
    // Store the image URL
    onChange?.(imageData.url);
    setImgDimensions({ w: imageData.width, h: imageData.height });

    // Calculate height based on image aspect ratio, using current node width
    setNodes((nodes: Node[]) =>
      nodes.map((node: Node) => {
        if (node.id === nodeId) {
          const currentWidth = node.width || 420; // Default width for ImagePanel nodes
          const aspectRatio = imageData.width / imageData.height;
          const calculatedHeight = Math.round(currentWidth / aspectRatio);

          return {
            ...node,
            height: calculatedHeight,
            data: {
              ...node.data,
              preloadedWidth: imageData.width,
              preloadedHeight: imageData.height,
            },
          };
        }
        return node;
      })
    );
  };

  const handleDelete = () => {
    recordUserAction('Remove image', { touchesResults: [nodeId] });
    onChange?.('');
    setImgDimensions(null);

    // Give the node back a default HEIGHT, and clear the stored image dimensions
    // so the aspect-ratio lock releases.
    //
    // Height, because the height being cleared was DERIVED from the image that
    // is going away (`handleImageUpload` writes `width / aspectRatio` into it).
    // Leaving it behind means a portrait image deleted from a 420x1000 panel
    // leaves a 1000px-tall empty drop zone.
    //
    // Not width, because width is a size the user chose and no image ever
    // overwrote it.
    //
    // On `node.height` and not `node.style.height`: React Flow v12 resolves the
    // rendered box as `node.height ?? node.style?.height`
    // (`getNodeInlineStyleDimensions`), so clearing only `style` — which is what
    // this did before — never reached the value actually in use. The `style`
    // reset is kept for the legacy shape, but it is the `height` field that does
    // the work.
    setNodes((nodes: Node[]) =>
      nodes.map((node: Node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            height: IMAGE_PANEL_DEFAULTS.height,
            style: {
              ...node.style,
              width: undefined,
              height: undefined,
            },
            data: {
              ...node.data,
              preloadedWidth: undefined,
              preloadedHeight: undefined,
            },
          };
        }
        return node;
      })
    );
  };

  // @ts-expect-error TS6133 - Kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleInpaint = () => {
    if (value) {
      setInpaintOpen(true);
    }
  };

  const handleInpaintAccept = (resultImageUrl: string) => {
    console.log('Inpaint accepted with result:', resultImageUrl);
    setInpaintOpen(false);
    
    // Get source node position
    const sourceNode = getNode(nodeId);
    if (!sourceNode) {
      console.error('Source node not found:', nodeId);
      return;
    }

    // Calculate position for new output node (offset to the right)
    const sourceWidth = sourceNode.measured?.width || sourceNode.width || 320;
    const newX = sourceNode.position.x + sourceWidth + 50;
    const newY = sourceNode.position.y;

    // Create new output node with proper structure (matching NodeAdapters.tsx pattern)
    const outputNodeId = `customOutput-${Date.now()}`;
    
    const initialPage = {
      content: resultImageUrl,
      context: null,
      type: 'image' as const,
      timestamp: new Date().toISOString()
    };
    
    const outputDims = getOutputNodeDimensions('image');
    const outputNode: Node = {
      id: outputNodeId,
      type: 'universalOutput',
      position: {
        x: newX,
        y: newY
      },
      width: outputDims.width,
      height: outputDims.height,
      data: {
        output: resultImageUrl,
        content: resultImageUrl,
        type: 'image',
        outputsMediaType: 'image',
        imageUrl: resultImageUrl,
        pages: [initialPage],
        currentPageIndex: 0,
        streamingInProgress: false
      }
    };

    // Create edge from source to new output
    const edge = {
      id: `${nodeId}-${outputNodeId}`,
      source: nodeId,
      target: outputNodeId,
      type: 'image',
      animated: true,
      style: { stroke: '#42C3D7', strokeWidth: 3 }
    };

    // Add node and edge to canvas
    setNodes((nodes) => [...nodes, outputNode]);
    setEdges((edges) => [...edges, edge]);
    
    console.log('Created universalOutput node:', outputNodeId, 'with image URL');
  };

  const handleImageClick = () => {
    if (value && !disabled) {
      setLightboxOpen(true);
    }
  };

  return (
    <>
      <Box
        className="image-container"
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'auto',
        }}
      >
        <Box onClick={handleImageClick} onContextMenu={handleContextMenu} sx={{ width: '100%', height: '100%' }}>
          <ImageUploadZone
            onImageUpload={handleImageUpload}
            onUploadStateChange={(uploading) => {
              setNodes((nodes: Node[]) =>
                nodes.map((node: Node) =>
                  node.id === nodeId ? { ...node, data: { ...node.data, isUploading: uploading } } : node
                )
              );
            }}
            currentImage={value}
            disabled={disabled}
            emptyHeight={emptyHeight}
          />
        </Box>

        {/* Loading overlay for paste upload */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
              zIndex: 10,
              gap: 1,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#10b981' }} />
            <Typography variant="caption" sx={{ color: 'white', fontSize: '11px' }}>
              Uploading...
            </Typography>
          </Box>
        )}

        {imgDimensions && value && !isUploading && (
          <div className="media-resolution-badge">
            {imgDimensions.w}×{imgDimensions.h}
          </div>
        )}
        <ImageActions
          show={!!value && !isUploading}
          onDelete={handleDelete}
          showSendToDevice={false}
          showDuplicate={false}
          showCopyToClipboard={true}
          onCopyToClipboard={() => {
            if (!value) return;
            copyImageUrlToClipboard(value)
              .catch(err => console.error('Failed to copy image:', err));
          }}
        />
      </Box>

      {lightboxOpen && value && (
        <Lightbox 
          imageUrl={value} 
          onClose={() => setLightboxOpen(false)}
          onDownload={handleDownload}
          onSendToDevice={onSendToDevice ? () => {
            onSendToDevice(value, 'image');
            setLightboxOpen(false);
          } : undefined}
          onSetAsThumbnail={onSetFlowThumbnail ? () => {
            onSetFlowThumbnail(value);
            setLightboxOpen(false);
          } : undefined}
          showThumbnailButton={true}
          onDelete={() => {
            handleDelete();
            setLightboxOpen(false);
          }}
          onCopyToClipboard={() => {
            if (value) {
              copyImageUrlToClipboard(value)
                .catch(err => console.error('Failed to copy image:', err));
            }
          }}
        />
      )}

      {inpaintOpen && value && (
        <InpaintEditor
          imageUrl={value}
          onClose={() => setInpaintOpen(false)}
          onAccept={handleInpaintAccept}
        />
      )}

      {/* Context Menu Portal */}
      {contextMenu && createPortal(
        <div
          ref={contextMenuRef}
          className="paged-output-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000,
          }}
        >
          {onSendToDevice && (
            <button
              className="context-menu-item"
              onClick={() => {
                onSendToDevice(value!, 'image');
                setContextMenu(null);
              }}
            >
              <PhoneIphoneIcon fontSize="small" />
              <span>Send to Device</span>
            </button>
          )}
          <button
            className="context-menu-item"
            onClick={() => {
              if (value) {
                copyImageUrlToClipboard(value)
                  .catch(err => console.error('Failed to copy image:', err));
              }
              setContextMenu(null);
            }}
          >
            <ContentCopyIcon fontSize="small" />
            <span>Copy to Clipboard</span>
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              handleDownload();
              setContextMenu(null);
            }}
          >
            <DownloadIcon fontSize="small" />
            <span>Download</span>
          </button>
          {onSetFlowThumbnail && (
            <button
              className="context-menu-item"
              onClick={() => {
                onSetFlowThumbnail(value!);
                setContextMenu(null);
              }}
            >
              <ImageIcon fontSize="small" />
              <span>Set Knod Thumbnail</span>
            </button>
          )}
          <div className="context-menu-divider" />
          <button
            className="context-menu-item context-menu-item--danger"
            onClick={() => {
              handleDelete();
              setContextMenu(null);
            }}
          >
            <DeleteIcon fontSize="small" />
            <span>Delete</span>
          </button>
        </div>,
        document.body
      )}
    </>
  );
};
